import { ReactNode } from 'react';
import { AlertTriangle, Inbox, RefreshCw, LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FirstPartyAnalyticsRange } from '@/services/analyticsService';

const RANGE_VALUES: FirstPartyAnalyticsRange[] = ['1d', '7d', '30d', '90d'];

interface AnalyticsPageHeaderProps {
  title: string;
  description: string;
  range: FirstPartyAnalyticsRange;
  onRangeChange: (range: FirstPartyAnalyticsRange) => void;
  rangeLabel: string;
  children?: ReactNode;
}

export function AnalyticsPageHeader({
  title,
  description,
  range,
  onRangeChange,
  rangeLabel,
  children,
}: AnalyticsPageHeaderProps) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        <p className="text-muted-foreground">{description}</p>
        {children}
      </div>
      <AnalyticsRangeTabs range={range} onRangeChange={onRangeChange} rangeLabel={rangeLabel} />
    </div>
  );
}

export function AnalyticsRefreshControl({
  refreshLabel,
  hintLabel,
  refreshing,
  disabled,
  onRefresh,
}: {
  refreshLabel: string;
  hintLabel: string;
  refreshing: boolean;
  disabled?: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3">
      <span className="text-xs text-muted-foreground">{hintLabel}</span>
      <Button
        variant="outline"
        size="sm"
        className="h-7"
        onClick={onRefresh}
        disabled={disabled || refreshing}
      >
        <RefreshCw className={`mr-2 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
        {refreshLabel}
      </Button>
    </div>
  );
}

export function AnalyticsRangeTabs({
  range,
  onRangeChange,
  rangeLabel,
}: {
  range: FirstPartyAnalyticsRange;
  onRangeChange: (range: FirstPartyAnalyticsRange) => void;
  rangeLabel: string;
}) {
  return (
    <Tabs value={range} onValueChange={(value) => onRangeChange(value as FirstPartyAnalyticsRange)}>
      <TabsList aria-label={rangeLabel} className="grid w-full grid-cols-4 sm:w-auto">
        {RANGE_VALUES.map((value) => (
          <TabsTrigger key={value} value={value} className="min-w-12">
            {value.toUpperCase()}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

interface MetricCardProps {
  title: string;
  value: string | number;
  description: string;
  icon: LucideIcon;
  comparisonLabel?: string;
  comparisonValue?: string;
}

export function MetricCard({ title, value, description, icon: Icon, comparisonLabel, comparisonValue }: MetricCardProps) {
  return (
    <Card className="overflow-hidden border-l-4 border-l-primary bg-gradient-to-br from-card to-background">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="mt-2 truncate text-3xl font-bold">{value}</p>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{description}</p>
            {comparisonLabel && comparisonValue ? (
              <div className="mt-3 border-t border-border/60 pt-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{comparisonLabel}</p>
                <p className="mt-1 text-xs font-medium text-foreground">{comparisonValue}</p>
              </div>
            ) : null}
          </div>
          <div className="rounded-full bg-primary/10 p-3 text-primary">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AnalyticsLoadingState({ label }: { label: string }) {
  return (
    <div className="space-y-4" aria-busy="true" aria-live="polite">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[1, 2, 3, 4].map((item) => (
          <Skeleton key={item} className="h-32 w-full" />
        ))}
      </div>
      <Skeleton className="h-[320px] w-full" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function AnalyticsEmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-md border bg-background p-6 text-center">
      <div className="rounded-full bg-muted p-3 text-muted-foreground">
        <Inbox className="h-7 w-7" aria-hidden="true" />
      </div>
      <div>
        <h3 className="font-medium">{title}</h3>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

export function AnalyticsErrorState({
  title,
  message,
  retryLabel,
  onRetry,
}: {
  title: string;
  message: string;
  retryLabel: string;
  onRetry: () => void;
}) {
  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span>{message}</span>
          <Button type="button" variant="outline" size="sm" onClick={onRetry} className="shrink-0">
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            {retryLabel}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}

export function ChartCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card className="bg-background border border-border/50 shadow-subtle">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
